import { APP_HTTP_STATUS } from './../_enums/status.enum';
import { catchError, tap } from 'rxjs/operators';
import { NotificationsService } from './../_services/notifications.service';
import { Injectable, NgZone } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { TooltipStatusEnum } from '../_enums/tooltip-status.enum';
import { TranslateService } from '@ngx-translate/core';
import { BlockUiService } from '../_services/auto-block-ui.service';
import { Store } from '@ngxs/store';
import { SocketService } from '../_services/socket.service';

@Injectable()
export class StatusResponseInterceptor implements HttpInterceptor {
    errorStatuses = [APP_HTTP_STATUS.IN_PROCESSED_REGISTRATOR, APP_HTTP_STATUS.EVENTS_ACTIVE_FOR_REGISTRATOR, APP_HTTP_STATUS.REGISTRATOR_IS_OFFLINE, APP_HTTP_STATUS.UNAUTHORIZED];
    statusesForEmptyBody = [429, 500, 413, 200];

    constructor(
        private notificationsService: NotificationsService,
        private translateService: TranslateService,
        private zone: NgZone,
        private blockUiService: BlockUiService,
        private store: Store,
        private socketService: SocketService,
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            tap(async (evt: HttpResponse<any>) => {
                if (evt.body?.status === APP_HTTP_STATUS.SESSION_FREEZE) {
                    this.blockUiService.blockUi(true);

                    const translate = await this.translateService.get('disconnectBlock.sessionBlocked').toPromise();

                    this.zone.runOutsideAngular(() => {
                        setTimeout(() => {
                            this.notificationsService.onEmit(TooltipStatusEnum.load, true, translate);
                            this.blockUiService.openModal();
                        }, 100);
                    });

                    return;
                }

                if (this.errorStatuses.includes(evt.body?.status)) {
                    const translate = await this.translateService.get(evt.body?.message).toPromise();
                    const message = `${evt.url}<br>${translate || evt.body?.message}`;
                    this.zone.runOutsideAngular(() => {
                        setTimeout(() => {
                            this.notificationsService.onEmit(TooltipStatusEnum.error, false, message);
                        }, 100);
                    });
                }

                switch (evt?.body?.status as APP_HTTP_STATUS) {
                    case APP_HTTP_STATUS.ACCESS_DENIED:
                        const accessDenied = await this.translateService.get('permissions.accessDenied').toPromise();
                        this.zone.runOutsideAngular(() => {
                            setTimeout(() => {
                                this.notificationsService.onEmit(TooltipStatusEnum.error, false, accessDenied);
                            }, 100);
                        });
                        break;
                    case APP_HTTP_STATUS.REGISTRATOR_IN_SYNC:
                        const messageSync = await this.translateService.get('devices.registratorIsSync').toPromise();
                        this.zone.runOutsideAngular(() => {
                            setTimeout(() => {
                                this.notificationsService.onEmit(TooltipStatusEnum.error, false, messageSync);
                            }, 100);
                        });
                        break;
                }

                const hasSpecificErrorHandler = this.errorStatuses.includes(evt.body?.status)
                    || evt.body?.status === APP_HTTP_STATUS.ACCESS_DENIED
                    || evt.body?.status === APP_HTTP_STATUS.REGISTRATOR_IN_SYNC;

                if (evt.body?.message && !hasSpecificErrorHandler) {
                    await this.notifyTranslatedError(evt.body.message);
                }
            }),
            catchError((event: HttpErrorResponse) => {
                const body = event.error && typeof event.error === 'object' ? event.error : this.modifyBody(event.status);
                const d = new HttpResponse({ body });
                if (event.status === 401) {
                    return throwError(() => new HttpErrorResponse(event));
                }

                if (event.url?.indexOf('assets/version.json') !== -1) {
                    return of(d);
                }

                if (event.status === 429) {
                    this.notificationsService.onEmit(TooltipStatusEnum.error, false, 'registratorLogs.errorToaster');
                    return of(d);
                }

                if (body?.message !== 'Mnemonic schemes limit is 3') {
                    void this.notifyTranslatedError(body?.message, `server error: ${event.status}`);
                }

                return of(d);
            }),
        );
    }

    private async notifyTranslatedError(messageKey?: string, fallbackMessage?: string): Promise<void> {
        if (!messageKey && !fallbackMessage) {
            return;
        }

        const translatedMessage = messageKey ? await this.translateService.get(messageKey).toPromise() : null;
        const message = translatedMessage && translatedMessage !== messageKey
            ? translatedMessage
            : messageKey || fallbackMessage;

        this.notificationsService.onEmit(TooltipStatusEnum.error, false, message);
    }

    private modifyBody(statusErr: number): any {
        const currentStatus = this.statusesForEmptyBody.find((status) => Math.abs(status) === statusErr);
        return currentStatus ? { status: currentStatus } : { status: APP_HTTP_STATUS.ERROR };
    }
}
